import {useMemo} from 'react';

import {useCachedDataWithOrigin} from '../../../util/cache';
import type * as QueryTypes from '../../../util/queryTypes';
import {useDetectPanelVariant} from '../../Panel/hooks/hooks';
import {usePanelContext} from '../../Panel/PanelContextProvider';
import {useIsRenderingInPopup} from '../../Panel/PanelEditor/SinglePanelInspectorContainer';
import {PanelPopupMode} from '../../Panel/types';
import {useBucketedData} from '../bucketedData/useBucketedData';
import {type Range} from '../common';
import {type RunsLinePlotConfig} from '../types';
type CachedBucketedDataProps = {
  pageQuery: QueryTypes.Query;
  runsLinePlotConfig: RunsLinePlotConfig;
  queryZoomRange: Range;
  numBuckets?: number;
};
export const useCachedBucketedData = ({
  pageQuery,
  runsLinePlotConfig,
  queryZoomRange,
  numBuckets,
}: CachedBucketedDataProps) => {
  const {panelPopupMode} = usePanelContext();
  const isRenderingInPopup = useIsRenderingInPopup();
  const {isFullScreenPanelPage} = useDetectPanelVariant();
  const isFullscreenMode =
    (isRenderingInPopup && panelPopupMode === PanelPopupMode.FULL_SCREEN) ||
    isFullScreenPanelPage;
  /**
   * `waitUntilPanelMeasurement` is a hack to delay launching the queries until we get the initial panel measurements because panel dimensions are calculated after the first render, and the first render would dispatch the queries at full resolution, we need to force a loading state to keep the panel from erroring and
   */

  const waitUntilPanelMeasurement = numBuckets ? false : true;

  /**
   * The data produced from this query is sent into the cache.
   * DO NOT READ DATA FROM THIS QUERY DIRECTLY. The cache is responsible for managing the data.
   * 1. If we don't have any data from the query, we return the cached data if it exists
   * 2. If we get new data from the server, we update the internal state in the cache
   * 3. The cache will return this stable internal state UNTIL a new server data object is sent through
   * While switching back and forth between the cachedData and the serverData should not matter (the cache data IS the server data), reading the data from two places makes this behavior harder to reason about
   * The LOADING and ERROR states can be used since we're not cach
   */
  const oneKQuery = useBucketedData({
    config: runsLinePlotConfig,
    pageQuery,
    queryZoomRange,
    nBuckets: numBuckets ?? 1000,
    skip: waitUntilPanelMeasurement,
  });

  /**
   * The 10k data can be used directly because we don't cache this
   */
  const tenKQuery = useBucketedData({
    config: runsLinePlotConfig,
    pageQuery,
    queryZoomRange,
    nBuckets: 10000,
    skip: waitUntilPanelMeasurement || !isFullscreenMode,
  });

  const {data: oneKData, origin} = useCachedDataWithOrigin<
    typeof oneKQuery.data
  >({
    namespace: 'BucketedRunsDeltaQuery',
    key: oneKQuery.key,
    dataFromServer: oneKQuery.data,
    waitingForDataFromServer: oneKQuery.loading,
  });

  let loading1k = oneKQuery.loading;
  // we flip off the loading state manually when we've got a cache hit
  if (origin === 'cache' && oneKData.histories.data.length > 0) {
    loading1k = false;
  }

  const has10kData = useMemo(
    () =>
      tenKQuery.data.histories.data.some(
        run => run.history && run.history.length > 0
      ),
    [tenKQuery.data.histories.data]
  );

  // Memoize the data to provide stable references
  const stableData = useMemo(() => {
    if (isFullscreenMode) {
      // only use the 1k data if we're waiting for the initial hit of the 10k data to load
      return has10kData ? tenKQuery.data : oneKData;
    }
    return oneKData;
  }, [isFullscreenMode, tenKQuery.data, oneKData, has10kData]);

  // loading doesn't need memoization since it's a primitive value and cheap to compute
  let loading = loading1k || waitUntilPanelMeasurement;
  if (isFullscreenMode) {
    loading = tenKQuery.loading || waitUntilPanelMeasurement;
  }

  // Compute error state
  const error = useMemo(() => {
    if (isFullscreenMode) {
      return tenKQuery.error || oneKQuery.error;
    }
    return oneKQuery.error;
  }, [isFullscreenMode, tenKQuery.error, oneKQuery.error]);

  // Memoize the entire return object to prevent unnecessary re-renders
  // when only loading state changes but data remains the same
  return useMemo(
    () => ({
      data: stableData,
      loading,
      error,
    }),
    [stableData, loading, error]
  );
};
